import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, CheckCircle, AlertCircle, Loader2, Pencil, X, MapPin, Paperclip, BookOpen } from 'lucide-react';

interface Document {
  documentId: string; documentTitle: string; fileName: string;
  documentType: string; province: string | null; city: string | null;
  documentUrl: string | null; uploadedAt: string; chunkCount: number; hasFile?: boolean;
}

const PROVINCES = ['Alberta','British Columbia','Manitoba','New Brunswick',
  'Newfoundland and Labrador','Northwest Territories','Nova Scotia',
  'Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'];

const inputCls =
  'w-full box-border rounded-[10px] border border-[var(--glass-border)] bg-white/5 px-[14px] py-[10px] text-[14px] text-[color:var(--text-primary)] outline-none font-[inherit] transition-[border-color] duration-150 focus:border-[var(--accent)]';

const PDFUpload: React.FC = () => {
  const [file, setFile]                   = useState<File | null>(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType]   = useState<'Context'|'Project'>('Project');
  const [province, setProvince]           = useState('');
  const [city, setCity]                   = useState('');
  const [documentUrl, setDocumentUrl]     = useState('');
  const [uploading, setUploading]         = useState(false);
  const [status, setStatus]               = useState<{ type:'success'|'error'|null; msg:string }>({ type:null, msg:'' });
  const [documents, setDocuments]         = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs]     = useState(true);
  const [editingDoc, setEditingDoc]       = useState<Document | null>(null);
  const [editUrl, setEditUrl]             = useState('');
  const [editProvince, setEditProvince]   = useState('');
  const [editCity, setEditCity]           = useState('');
  const [updating, setUpdating]           = useState(false);
  const [dragOver, setDragOver]           = useState(false);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try { const r = await fetch('/api/pdf/documents'); setDocuments(await r.json()); }
    catch(e) { console.error(e); } finally { setLoadingDocs(false); }
  };

  const onFileChange = (f: File) => {
    setFile(f);
    if (!documentTitle) setDocumentTitle(f.name.replace(/\.pdf$/i,''));
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !documentTitle) { setStatus({ type:'error', msg:'Please select a file and provide a title' }); return; }
    setUploading(true); setStatus({ type:null, msg:'' });
    const fd = new FormData();
    fd.append('pdf', file); fd.append('documentTitle', documentTitle);
    fd.append('documentType', documentType);
    if (province) fd.append('province', province);
    if (city) fd.append('city', city);
    if (documentUrl) fd.append('documentUrl', documentUrl);
    try {
      const r = await fetch('/api/pdf/upload', { method:'POST', body: fd });
      const d = await r.json();
      if (r.ok) {
        setStatus({ type:'success', msg:`Processed ${d.totalChunks} chunks from "${d.documentTitle}"` });
        setFile(null); setDocumentTitle(''); setDocumentType('Project'); setProvince(''); setCity(''); setDocumentUrl('');
        fetchDocs();
      } else { setStatus({ type:'error', msg: d.message||'Upload failed' }); }
    } catch { setStatus({ type:'error', msg:'Network error. Please try again.' }); }
    finally { setUploading(false); }
  };

  const attachFile = async (id: string, file: File | undefined) => {
    if (!file) return;
    const fd = new FormData(); fd.append('pdf', file);
    setStatus({ type: 'success', msg: 'Attaching PDF…' });
    try {
      const r = await fetch(`/api/pdf/document/${id}/file`, { method: 'POST', body: fd });
      if (r.ok) { setStatus({ type: 'success', msg: 'PDF attached' }); fetchDocs(); }
      else { const e = await r.json().catch(() => ({})); setStatus({ type: 'error', msg: e.error || 'Attach failed' }); }
    } catch { setStatus({ type: 'error', msg: 'Attach failed' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this document and all its chunks?')) return;
    try {
      const r = await fetch(`/api/pdf/document/${id}`, { method:'DELETE' });
      if (r.ok) { setDocuments(p => p.filter(d => d.documentId!==id)); setStatus({ type:'success', msg:'Document deleted' }); }
      else { setStatus({ type:'error', msg:'Delete failed' }); }
    } catch { setStatus({ type:'error', msg:'Network error' }); }
  };

  const handleUpdate = async () => {
    if (!editingDoc) return;
    setUpdating(true);
    try {
      const r = await fetch(`/api/pdf/document/${editingDoc.documentId}`, {
        method:'PUT', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ documentUrl: editUrl||null, province: editProvince||null, city: editCity||null }),
      });
      if (r.ok) { setStatus({ type:'success', msg:'Document updated' }); setEditingDoc(null); fetchDocs(); }
      else { setStatus({ type:'error', msg:'Update failed' }); }
    } catch { setStatus({ type:'error', msg:'Network error' }); }
    finally { setUpdating(false); }
  };

  const canSubmit = !uploading && !!file && !!documentTitle;

  return (
    <div className="min-h-full bg-transparent p-8">
      <div className="mx-auto flex max-w-[900px] flex-col gap-6">
        <div>
          <h1 className="m-0 mb-1 text-[22px] font-semibold text-[color:var(--text-primary)]">Document Upload</h1>
          <p className="m-0 text-[13px] text-[color:var(--text-muted)]">Upload PDFs to make them searchable in the AI chat</p>
        </div>

        {/* Upload Form */}
        <div className="flex flex-col gap-5 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-7 backdrop-blur-[12px]">
          <form onSubmit={handleUpload} className="flex flex-col gap-5">
            {/* Dropzone */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">PDF File *</label>
              <div
                className="cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-all duration-150"
                style={{ borderColor: dragOver ? '#10b981' : (file ? 'rgba(16,185,129,0.4)' : '#27272a'), background: dragOver ? 'rgba(16,185,129,0.05)' : 'transparent' }}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f?.type==='application/pdf') onFileChange(f); }}
                onClick={() => document.getElementById('pdfInput')?.click()}
              >
                <input id="pdfInput" type="file" accept=".pdf" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onFileChange(f); }} />
                <Upload size={28} color={file ? '#10b981' : '#3f3f46'} className="mx-auto mb-2.5" />
                <p className="m-0 mb-1 text-[14px]" style={{ color: file ? '#34d399' : '#52525b' }}>
                  {file ? file.name : 'Drop PDF here or click to browse'}
                </p>
                <p className="m-0 text-[12px] text-[color:var(--border-subtle)]">Max 50MB</p>
              </div>
            </div>

            {/* Title */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Document Title *</label>
              <input value={documentTitle} onChange={e => setDocumentTitle(e.target.value)} placeholder="e.g., Municipal Strategic Plan 2024" className={inputCls} />
            </div>

            {/* Type */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Document Type *</label>
              <div className="grid grid-cols-2 gap-2.5">
                {([['Context','#60a5fa','General NAM knowledge, always relevant'],['Project','#34d399','Location-specific implementation']] as const).map(([t,c,d]) => {
                  const active = documentType===t;
                  return (
                    <button key={t} type="button" onClick={() => setDocumentType(t as any)}
                      className="cursor-pointer rounded-xl border-2 px-4 py-3.5 text-left transition-all duration-150"
                      style={{ borderColor: active ? c : '#27272a', background: active ? `${c}18` : '#18181b' }}>
                      <p className="m-0 mb-0.5 text-[14px] font-semibold" style={{ color: active ? c : '#a1a1aa' }}>{t}</p>
                      <p className="m-0 text-[12px] text-[color:var(--text-muted)]">{d}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Province & City */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Province</label>
                <select value={province} onChange={e => setProvince(e.target.value)}
                  className="w-full box-border cursor-pointer rounded-[10px] border border-[var(--glass-border)] bg-white/5 px-[14px] py-[10px] text-[14px] text-[color:var(--text-secondary)] outline-none font-[inherit]">
                  <option value="">Select province…</option>
                  {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">City</label>
                <input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g., Vancouver" className={inputCls} />
              </div>
            </div>

            {/* URL */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Source URL</label>
              <input type="url" value={documentUrl} onChange={e => setDocumentUrl(e.target.value)} placeholder="https://example.com/document.pdf" className={inputCls} />
            </div>

            {status.type && (
              <div className={
                'flex items-center gap-2.5 rounded-[10px] px-4 py-3 text-[13px] ' +
                (status.type==='success'
                  ? 'border border-[var(--accent-border)] bg-[var(--accent-dim)] text-[var(--accent-strong)]'
                  : 'border border-[var(--danger-dim)] bg-[var(--danger-dim)] text-[var(--danger-strong)]')
              }>
                {status.type==='success' ? <CheckCircle size={16}/> : <AlertCircle size={16}/>}
                {status.msg}
              </div>
            )}

            <button type="submit" disabled={!canSubmit}
              className={
                'flex w-full items-center justify-center gap-2 rounded-[10px] border-none px-6 py-3 text-[14px] font-semibold transition-all duration-150 ' +
                (canSubmit
                  ? 'cursor-pointer bg-[var(--accent)] text-white hover:bg-[var(--accent-strong)]'
                  : 'cursor-not-allowed bg-[var(--surface)] text-[color:var(--border-subtle)]')
              }
            >
              {uploading ? <><Loader2 size={16} style={{ animation:'spin 0.7s linear infinite' }}/> Processing…</> : <><Upload size={16}/> Upload & Process</>}
            </button>
          </form>
        </div>

        {/* Documents List */}
        <div className="flex flex-col gap-5 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-7 backdrop-blur-[12px]">
          <h2 className="m-0 text-[16px] font-semibold text-[color:var(--text-primary)]">Uploaded Documents</h2>
          {loadingDocs ? (
            <div className="flex justify-center p-8">
              <Loader2 size={24} color="#52525b" style={{ animation:'spin 0.7s linear infinite' }} />
            </div>
          ) : documents.length===0 ? (
            <p className="m-0 py-8 text-center text-[14px] text-[color:var(--border-subtle)]">No documents uploaded yet</p>
          ) : (
            <div className="flex flex-col gap-2">
              {documents.map(doc => (
                <div key={doc.documentId}
                  className="flex items-center justify-between gap-3 rounded-xl border border-[var(--glass-border)] bg-white/[0.04] p-4 transition-[border-color] duration-150 hover:border-[var(--border-subtle)]"
                >
                  <FileText size={20} color="#52525b" className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <p className="m-0 mb-0.5 text-[14px] font-semibold text-[color:var(--text-primary)]">{doc.documentTitle}</p>
                      <span
                        className="rounded-full border px-2 py-0.5 text-[11px] font-medium"
                        style={
                          doc.documentType==='Context'
                            ? { background:'rgba(59,130,246,0.1)', color:'#60a5fa', borderColor:'rgba(59,130,246,0.2)' }
                            : { background:'rgba(16,185,129,0.1)', color:'#34d399', borderColor:'rgba(16,185,129,0.2)' }
                        }
                      >{doc.documentType}</span>
                    </div>
                    <p className="m-0 flex flex-wrap gap-3 text-[12px] text-[color:var(--text-muted)]">
                      <span>{doc.chunkCount} chunks</span>
                      {doc.province && <span><MapPin size={10} className="mr-0.5 inline"/>{doc.city ? `${doc.city}, ` : ''}{doc.province}</span>}
                      <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {doc.hasFile && (
                      <a href={`/api/pdf/file/${doc.documentId}`} target="_blank" rel="noreferrer" title="Read PDF" className="flex rounded-lg p-2 text-[#34d399] transition-all duration-150 hover:bg-[rgba(16,185,129,0.1)]"><BookOpen size={15}/></a>
                    )}
                    <label title={doc.hasFile ? 'Replace PDF' : 'Attach source PDF'} className="flex cursor-pointer rounded-lg p-2 text-[color:var(--text-muted)] transition-all duration-150 hover:bg-white/[0.06] hover:text-[color:var(--text-primary)]">
                      <Paperclip size={15}/>
                      <input type="file" accept="application/pdf" className="hidden" onChange={e => { attachFile(doc.documentId, e.target.files?.[0]); e.currentTarget.value=''; }} />
                    </label>
                    <button className="flex cursor-pointer rounded-lg border-none bg-transparent p-2 text-[#60a5fa] transition-all duration-150 hover:bg-[rgba(59,130,246,0.1)]" title="Edit" onClick={() => { setEditingDoc(doc); setEditUrl(doc.documentUrl||''); setEditProvince(doc.province||''); setEditCity(doc.city||''); }}
                    ><Pencil size={15}/></button>
                    <button className="flex cursor-pointer rounded-lg border-none bg-transparent p-2 text-[var(--danger-strong)] transition-all duration-150 hover:bg-[var(--danger-dim)]" title="Delete" onClick={() => handleDelete(doc.documentId)}
                    ><Trash2 size={15}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={e => e.target===e.currentTarget && setEditingDoc(null)}>
          <div className="flex w-full max-w-[500px] flex-col gap-5 rounded-[20px] border border-[var(--glass-border)] bg-[var(--glass-bg)] p-7 backdrop-blur-[20px]">
            <div className="flex items-center justify-between">
              <h3 className="m-0 text-[18px] font-semibold text-[color:var(--text-primary)]">Edit Document</h3>
              <button className="flex cursor-pointer border-none bg-none p-1 text-[color:var(--text-muted)]" onClick={() => setEditingDoc(null)}><X size={18}/></button>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Source URL</label>
                <input type="url" value={editUrl} onChange={e => setEditUrl(e.target.value)} placeholder="https://…" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Province</label>
                  <select value={editProvince} onChange={e => setEditProvince(e.target.value)}
                    className="w-full box-border cursor-pointer rounded-[10px] border border-[var(--glass-border)] bg-white/5 px-[14px] py-[10px] text-[14px] text-[color:var(--text-secondary)] outline-none font-[inherit]">
                    <option value="">Select…</option>
                    {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-medium uppercase tracking-[0.06em] text-[color:var(--text-muted)]">City</label>
                  <input value={editCity} onChange={e => setEditCity(e.target.value)} placeholder="Vancouver" className={inputCls} />
                </div>
              </div>
            </div>
            <div className="flex gap-2.5">
              <button onClick={handleUpdate} disabled={updating}
                className="flex-1 cursor-pointer rounded-[10px] border-none bg-[var(--accent)] px-6 py-2.5 text-[14px] font-semibold text-white transition-colors duration-150 disabled:opacity-60"
              >
                {updating ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={() => setEditingDoc(null)}
                className="cursor-pointer rounded-[10px] border border-[var(--border)] bg-[var(--surface)] px-6 py-2.5 text-[14px] font-medium text-[color:var(--text-muted)] transition-all duration-150"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default PDFUpload;
